// Fuzzy dates (PLAN §2): stored with precision and openness, parsed from free text.
// Accepts what analysts actually write (see the real profile sheets): "2021-10-06", "October 7, 2023",
// "Oct 16, 2024", "11 September 2025", "21/05/2019" (day first), "2017–2024", "since 2014", "ongoing",
// "c. 2021", "spring 2019", "mid-2022", "Q3 2023", French and English month names.

export type DatePrecision = 'day' | 'month' | 'quarter' | 'season' | 'year' | 'decade';

export interface FuzzyPoint {
  /** ISO prefix: YYYY, YYYY-MM or YYYY-MM-DD. Quarters/seasons store their first month. */
  value: string;
  precision: DatePrecision;
  approximate?: boolean;
  /** For quarter/season/early/mid/late: the covered span as ISO dates. */
  span?: { from: string; to: string };
}

export interface FuzzyDate {
  from: FuzzyPoint | null;
  /** `null` with `openEnd` = ongoing ("since 2021"). Equal to `from` for single dates. */
  to: FuzzyPoint | null;
  openEnd: boolean;
  /** Human-readable interpretation shown before saving. */
  label: string;
  /** Original text, kept verbatim. */
  raw: string;
}

const MONTHS: Record<string, number> = {};
[
  ['january', 'jan', 'janvier', 'janv'],
  ['february', 'feb', 'février', 'fevrier', 'févr', 'fevr'],
  ['march', 'mar', 'mars'],
  ['april', 'apr', 'avril', 'avr'],
  ['may', 'mai'],
  ['june', 'jun', 'juin'],
  ['july', 'jul', 'juillet', 'juil'],
  ['august', 'aug', 'août', 'aout'],
  ['september', 'sep', 'sept', 'septembre'],
  ['october', 'oct', 'octobre'],
  ['november', 'nov', 'novembre'],
  ['december', 'dec', 'décembre', 'decembre', 'déc'],
].forEach((names, i) => {
  for (const n of names) MONTHS[n] = i + 1;
});

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const SEASONS: Record<string, [number, number]> = {
  spring: [3, 5],
  printemps: [3, 5],
  summer: [6, 8],
  été: [6, 8],
  ete: [6, 8],
  autumn: [9, 11],
  fall: [9, 11],
  automne: [9, 11],
  winter: [12, 2],
  hiver: [12, 2],
};
const PART: Record<string, [number, number]> = { early: [1, 4], mid: [5, 8], late: [9, 12], début: [1, 4], debut: [1, 4], fin: [9, 12] };

const pad = (n: number) => String(n).padStart(2, '0');
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const validDay = (y: number, m: number, d: number) => m >= 1 && m <= 12 && d >= 1 && d <= lastDay(y, m);
const monthOf = (s: string) => MONTHS[s.toLowerCase().replace(/\.$/, '')];
const spanOf = (y: number, m1: number, m2: number) => {
  const y2 = m2 < m1 ? y + 1 : y;
  return { from: `${y}-${pad(m1)}-01`, to: `${y2}-${pad(m2)}-${pad(lastDay(y2, m2))}` };
};

const YEAR = '(\\d{4})';
const MONTH_WORD = '([A-Za-zÀ-ÿ]{3,9}\\.?)';

type Rule = [RegExp, (m: RegExpMatchArray) => FuzzyPoint | null];

const dayPoint = (y: number, mo: number | undefined, d: number): FuzzyPoint | null =>
  mo && validDay(y, mo, d) ? { value: `${y}-${pad(mo)}-${pad(d)}`, precision: 'day' } : null;

/** Point formats, tried in order. */
const POINT_RULES: Rule[] = [
  // ISO: 2021-10-06 · 2021-10 · 2021/10/06
  [
    /^(\d{4})[-/.](\d{1,2})(?:[-/.](\d{1,2}))?$/,
    m => {
      const [y, mo] = [Number(m[1]), Number(m[2])];
      if (mo < 1 || mo > 12) return null;
      return m[3] ? dayPoint(y, mo, Number(m[3])) : { value: `${y}-${pad(mo)}`, precision: 'month' };
    },
  ],
  // Day first (European): 21/05/2019 · 21.05.2019 · 21-05-2019
  [/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/, m => dayPoint(Number(m[3]), Number(m[2]), Number(m[1]))],
  // Year: 2024
  [/^(\d{4})$/, m => ({ value: m[1] ?? '', precision: 'year' })],
  // Decade: 1990s · the 1990s
  [/^(?:the )?(\d{3})0s$/i, m => ({ value: `${m[1]}0`, precision: 'decade', span: { from: `${m[1]}0-01-01`, to: `${m[1]}9-12-31` } })],
  // Month name + day + year: October 7, 2023 · Oct 16 2024 · Dec. 3, 2015
  [new RegExp(`^${MONTH_WORD} (\\d{1,2})(?:st|nd|rd|th)?,? ${YEAR}$`, 'i'), m => dayPoint(Number(m[3]), monthOf(m[1] ?? ''), Number(m[2]))],
  // Day + month name + year: 11 September 2025 · 9 Jan 2019 · 1er mars 2021
  [
    new RegExp(`^(\\d{1,2})(?:er|st|nd|rd|th)? ${MONTH_WORD},? ${YEAR}$`, 'i'),
    m => dayPoint(Number(m[3]), monthOf(m[2] ?? ''), Number(m[1])),
  ],
  // Month or season + year: March 1971 · oct. 2021 · octobre 2021 · spring 2019
  [
    new RegExp(`^${MONTH_WORD},? ${YEAR}$`, 'i'),
    m => {
      const word = (m[1] ?? '').toLowerCase();
      const y = Number(m[2]);
      const mo = monthOf(word);
      if (mo) return { value: `${y}-${pad(mo)}`, precision: 'month' };
      const season = SEASONS[word];
      return season ? { value: `${y}-${pad(season[0])}`, precision: 'season', span: spanOf(y, season[0], season[1]) } : null;
    },
  ],
  // Quarter: Q3 2023 · 2023 Q3
  [/^Q([1-4]) (\d{4})$/i, m => quarter(Number(m[2]), Number(m[1]))],
  [/^(\d{4}) Q([1-4])$/i, m => quarter(Number(m[1]), Number(m[2]))],
  // Early / mid / late year: mid-2022 · early 2020 · fin 2021
  [
    /^(early|mid|late|début|debut|fin)[- ](\d{4})$/i,
    m => {
      const part = PART[(m[1] ?? '').toLowerCase()];
      const y = Number(m[2]);
      return part ? { value: `${y}-${pad(part[0])}`, precision: 'year', approximate: true, span: spanOf(y, part[0], part[1]) } : null;
    },
  ],
];

function quarter(y: number, q: number): FuzzyPoint {
  const m1 = (q - 1) * 3 + 1;
  return { value: `${y}-${pad(m1)}`, precision: 'quarter', span: spanOf(y, m1, m1 + 2) };
}

/** Parses one point in time ("2021-10", "c. 2021", "October 7, 2023"...). Returns null if not a date. */
export function parseFuzzyPoint(input: string): FuzzyPoint | null {
  let s = input.trim().replace(/\s+/g, ' ');
  let approximate = false;
  const approx = s.match(/^(?:c\.|ca\.|circa|about|around|approx\.?|vers|env\.?|~)\s*(.+)$/i);
  if (approx?.[1]) {
    approximate = true;
    s = approx[1];
  }
  for (const [re, parse] of POINT_RULES) {
    const m = s.match(re);
    if (!m) continue;
    const p = parse(m);
    return p && approximate ? { ...p, approximate } : p;
  }
  return null;
}

/** Plain-language rendering of a point ("March 1971", "c. 2021", "Q3 2023"). */
export function formatFuzzyPoint(p: FuzzyPoint): string {
  const [y, mo, d] = p.value.split('-').map(Number) as [number, number | undefined, number | undefined];
  const month = mo ? MONTH_NAMES[mo - 1] : '';
  let text: string;
  switch (p.precision) {
    case 'day':
      text = `${d} ${month} ${y}`;
      break;
    case 'month':
      text = `${month} ${y}`;
      break;
    case 'quarter':
      text = `Q${Math.floor(((mo ?? 1) - 1) / 3) + 1} ${y}`;
      break;
    case 'season': {
      const name = Object.entries(SEASONS).find(([k, [a]]) => a === mo && /^[a-z]+$/.test(k) && k !== 'fall')?.[0] ?? '';
      text = `${name[0]?.toUpperCase()}${name.slice(1)} ${y}`;
      break;
    }
    case 'decade':
      text = `${y}s`;
      break;
    default:
      text = p.span && p.approximate ? `${({ 1: 'Early', 5: 'Mid', 9: 'Late' } as Record<number, string>)[mo ?? 1] ?? ''} ${y}` : `${y}`;
  }
  return p.approximate && !(p.precision === 'year' && p.span) ? `c. ${text}` : text;
}

/**
 * Parses free text into a fuzzy date or range. Returns null when the text isn't recognisable,
 * so the input can ask for a clearer value instead of guessing.
 */
export function parseFuzzyDate(input: string): FuzzyDate | null {
  const raw = input;
  const s = input.trim().replace(/\s+/g, ' ');
  if (!s) return null;

  if (/^(ongoing|present|current|currently|en cours|aujourd'hui)$/i.test(s))
    return { from: null, to: null, openEnd: true, label: 'Ongoing', raw };

  // Open-ended: since 2021-10 · from 2019 · depuis 2014 · 2021 → ongoing · 2021–present
  const open =
    s.match(/^(?:since|from|depuis|dès|starting) (.+)$/i) ??
    s.match(/^(.+?)\s*(?:→|->|–|—|-)\s*(?:ongoing|present|now|today|aujourd'hui)$/i);
  if (open?.[1]) {
    const from = parseFuzzyPoint(open[1]);
    return from ? { from, to: null, openEnd: true, label: `Since ${formatFuzzyPoint(from)} (ongoing)`, raw } : null;
  }

  const single = parseFuzzyPoint(s);
  if (single) return { from: single, to: single, openEnd: false, label: formatFuzzyPoint(single), raw };

  // Ranges: 2017–2024 · 2024-2025 · March 2019 to June 2020 · 2019-03 – 2020 · 21/05/2019 - 30/06/2019
  const candidates: [string, string][] = [];
  const years = s.match(/^(\d{4})\s*[-–—]\s*(\d{4})$/);
  if (years) candidates.push([years[1] ?? '', years[2] ?? '']);
  // Short range "2019-24"; "2019-02" is an ISO month, so only when the tail is > 12.
  const short = s.match(/^(\d{4})\s*[-–—]\s*(\d{2})$/);
  if (short && Number(short[2]) > 12) candidates.push([short[1] ?? '', `${(short[1] ?? '').slice(0, 2)}${short[2]}`]);
  const spaced = s.split(/\s+(?:–|—|-|to|until|till|au|à|jusqu'?à)\s+/i);
  if (spaced.length === 2) candidates.push([spaced[0] ?? '', spaced[1] ?? '']);
  const tight = s.split(/\s*[–—]\s*/);
  if (tight.length === 2) candidates.push([tight[0] ?? '', tight[1] ?? '']);
  for (const [a, b] of candidates) {
    const from = parseFuzzyPoint(a);
    const to = parseFuzzyPoint(b);
    if (from && to && compareFuzzy(from, to) <= 0)
      return { from, to, openEnd: false, label: `${formatFuzzyPoint(from)} – ${formatFuzzyPoint(to)}`, raw };
  }
  return null;
}

/** Earliest ISO day a point can mean, for sorting and range checks. */
export const fuzzyStart = (p: FuzzyPoint): string =>
  p.span?.from ?? (p.value.length === 4 ? `${p.value}-01-01` : p.value.length === 7 ? `${p.value}-01` : p.value);

const compareFuzzy = (a: FuzzyPoint, b: FuzzyPoint) => fuzzyStart(a).localeCompare(fuzzyStart(b));
